import { useEffect, useRef } from 'react'
import { api } from '../../api/endpoints'
import type { LocalChannel } from '../../db/db'
import { db } from '../../db/db'
import { hub, hubConnected } from '../../realtime/hub'
import { setFocusedChannel } from '../../realtime/subscriptions'
import { clearNotifications } from '../../push/clearNotifications'
import { isOnline } from '../../state/connection'

/** Tell the hub which channel is on screen so push is suppressed for it. */
export function useFocusedChannel(channelId: string): void {
  useEffect(() => {
    const update = () => setFocusedChannel(document.visibilityState === 'visible' ? channelId : null)
    update()
    document.addEventListener('visibilitychange', update)
    return () => {
      document.removeEventListener('visibilitychange', update)
      setFocusedChannel(null)
    }
  }, [channelId])
}

export async function markChannelRead(channelId: string, seq: number): Promise<void> {
  await db.channels.update(channelId, { lastReadSeq: seq, unread: 0 })
  // Read here: its notifications on this device are done with.
  void clearNotifications(channelId)
  if (hubConnected()) {
    if (await hub.markRead(channelId, seq)) return
  }
  if (isOnline()) await api.markRead(channelId, seq).catch(() => undefined)
}

/**
 * When the conversation is visible and scrolled to the bottom, mark the head as read (debounced).
 */
export function useMarkRead(channel: LocalChannel, atBottom: boolean): void {
  const lastSent = useRef(0)
  const { id, headSeq, lastReadSeq, isMember } = channel

  useEffect(() => {
    if (!isMember || !atBottom || headSeq <= lastReadSeq || headSeq <= lastSent.current) return
    let timer: ReturnType<typeof setTimeout> | null = null
    const attempt = () => {
      if (document.visibilityState !== 'visible') return
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        lastSent.current = headSeq
        void markChannelRead(id, headSeq)
      }, 700)
    }
    attempt()
    document.addEventListener('visibilitychange', attempt)
    window.addEventListener('focus', attempt)
    return () => {
      if (timer) clearTimeout(timer)
      document.removeEventListener('visibilitychange', attempt)
      window.removeEventListener('focus', attempt)
    }
  }, [id, headSeq, lastReadSeq, isMember, atBottom])
}
